import React, { useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import { StyledDialog } from './BottomSheetV2.styles';
import { Steps } from './components/Steps';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useScrollPosition } from '@shared/utils/hooks/useScrollPosition';
import { BottomSheetStateContext, BottomSheetProviderProps } from './BottomSheetV2.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type CreateBottomSheetArgs = Readonly<{
  name: string;
}>;

export function createBottomSheetV2<State extends Readonly<{ [key: string]: unknown }>>({ name }: CreateBottomSheetArgs) {
  const [Provider, useContext] = createContext<BottomSheetStateContext<State>>({
    name,
    strict: true,
  });

  function useBottomSheetState() {
    return useContext();
  }

  /**
   * Wrap our target with the Provider and pass the different props.
   * Since the BottomSheet works only on Mobile, make sure to have a Mobile View to communicate with the Sheet and
   * hide whatever existent Desktop Navigation Component.
   *
   * ```js
   * <BottomSheetProvider
   *   views={[
   *     <GenericView key={0} name="FIRST VIEW" />,
   *     <GenericView key={1} name="SECOND VIEW" />
   *   ]}
   * >
   *  <DesktopView />
   *  <MobileView />
   * </BottomSheetProvider>
   * ```
   *
   * Just an example for the implementation. This component will show the different views basing on the active step.
   * We use parentViewState to share information between the parent views and the bottom sheet views.
   *
   * ```js
   * const MobileView: React.FC = () => {
   * const isMobile = useIsMobileScreen();
   * const { step, parentViewState, setParentViewState } = useBottomSheetState();
   *
   * useEffect(() => {
   *   setParentViewState({ guests: 10, showDialog: false });
   * }, [setParentViewState]);
   *
   * if (!isMobile) return null;
   *
   * return (
   *   <div style={{ display: 'flex', marginTop: '200px', height: '1000px', flexDirection: 'column', alignItems: 'center' }}>
   *     <div>MOBILE VIEW - Step: {step}</div>
   *     <div>GUESTS: {parentViewState?.guests}</div>
   *     <div>SHOW DIALOG: {parentViewState?.showDialog ? 'true' : 'false'}</div>
   *   </div>
   * );
   * };
   * ```
   *
   * Just an example for the implementation. Here we will have the different views for the bottom sheet passed as props to the Provider.
   * We use parentViewState to share information between the parent views and the bottom sheet views.
   *
   * ```js
   * const GenericView: React.FC<{ name: string }> = ({ name }) => {
   * const { parentViewState, setParentViewState } = useBottomSheetState();
   *
   * return (
   *   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', marginTop: '10px' }}>
   *     <div>{name}</div>
   *     <div>GUESTS:{parentViewState?.guests}</div>
   *     <button
   *       style={{ borderColor: 'black', borderWidth: '2px', padding: '2px', borderRadius: '5%' }}
   *       onClick={() => setParentViewState(prevState => ({ ...prevState, showDialog: true }))}
   *     >
   *       Open Dialog
   *     </button>
   *   </div>
   * );
   * };
   * ```
   *
   * */

  function BottomSheetProvider({ children, initialParentViewState, ...props }: BottomSheetProviderProps<State>) {
    const scrollPosition = useScrollPosition(true);
    const [step, setStep] = useState<string>(props.views?.[0]?.id || '');
    const [parentViewState, setParentViewState] = useState<State>(() => initialParentViewState);
    const currentStepIndex = useMemo(() => props.views.findIndex(view => view.id === step), [step, props.views]);

    // Need to wrap the initial setState function because the state can be initially undefined
    const wrappedSetParentViewState: React.Dispatch<React.SetStateAction<State>> = useEventCallback(value => {
      setParentViewState(value);
    });

    const context = useMemo<BottomSheetStateContext<State>>(
      () => ({
        scrollPosition,
        currentStepIndex,
        step,
        setStep,
        parentViewState,
        setParentViewState: wrappedSetParentViewState,
        descriptionOffsetTransform: `translateY(${scrollPosition ? 20 : 0}px)`,
        ...props,
      }),
      [scrollPosition, currentStepIndex, step, wrappedSetParentViewState, parentViewState, props],
    );

    return <Provider value={context}>{children}</Provider>;
  }

  /**
   * This component works only on Mobile.
   * Make sure the container who wraps this component has overflow hidden.
   *
   * ```js
   * <Box overflow="hidden">
   *  <BottomSheet />
   * </Box>
   * ```
   * */
  const BottomSheet: React.FC<{ disableNavigation?: boolean; forceClosed?: boolean }> = ({ disableNavigation = false, forceClosed = false }) => {
    const isMobile = useIsMobileScreen();
    const { backgroundColor, maxHeight, minHeight, scrollPosition, step, setStep, views } = useBottomSheetState();

    if (!isMobile) return null;

    return (
      <StyledDialog
        $scrollPosition={scrollPosition}
        $maxHeight={maxHeight}
        $minHeight={minHeight}
        $backgroundColor={backgroundColor}
        $forceClosed={forceClosed}
      >
        <Steps
          maxHeight={maxHeight}
          views={views}
          step={step}
          setStep={setStep}
          disableNavigation={disableNavigation}
        />
      </StyledDialog>
    );
  };

  return [BottomSheetProvider, useBottomSheetState, BottomSheet] as const;
}
